import { useCallback, useLayoutEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { useColorScheme, useTheme } from "@mui/material/styles"
import Plotly from "plotly.js-dist-min"

import { PLOTLY_THEME_DEFAULTS } from "./constants"
import usePlotlyResizeObserver from "./usePlotlyResizeObserver"
import {
  applyAxisThemeToLayout,
  createThemeLayout,
  deepMerge,
  normalizePlotlyLayout,
  resolveThemeColors,
  resolveTopMargin,
} from "./plotly-utils"

export default function PlotlyChart({ data, layout, config, className }) {
  const containerRef = useRef(null)
  const isInitialized = useRef(false)
  // Resolve chrome from the active scheme's RESOLVED theme palette (synchronous, no DOM/timing race): a toggle
  // changes activeScheme → themeColors → renderPlot dep → deterministic re-plot.
  const theme = useTheme()
  const { mode, systemMode } = useColorScheme()
  // Follow the OS only when the user's choice is "system" (MUI leaves systemMode undefined otherwise); an
  // explicit light/dark mode always wins over the OS preference.
  const resolvedMode = mode === "system" ? systemMode : mode
  const activeScheme = resolvedMode === "dark" ? "dark" : "light"
  const themeColors = useMemo(() => resolveThemeColors(theme, activeScheme), [theme, activeScheme])

  // Clone the traces once per `data` change, not per render. The resize path re-runs the full render every
  // coalesced frame but `data` is unchanged there, so reusing one clone drops a `structuredClone` of the (often
  // large) trace array off every frame. Plotly mutates its own copy in place — never the caller's `data` prop.
  const clonedData = useMemo(() => structuredClone(data), [data])

  function isRenderable(container) {
    if (!container?.isConnected) {
      return false
    }
    const rect = container.getBoundingClientRect()
    // Guard against zero-size or display:none ancestors
    return rect.width > 0 && rect.height > 0
  }

  // Returns "not-renderable" when the container has zero size / a display:none ancestor, so the serialized
  // caller (runRender) can retry on the next frame WITHOUT this scheduling its own rAF — a self-scheduled retry
  // would run outside the in-flight latch and could overlap a later Plotly.react on the same node.
  const renderPlot = useCallback(async function renderPlotInternal(container) {
    if (!container?.isConnected) {
      return "done"
    }

    if (!isRenderable(container)) {
      return "not-renderable"
    }

    const incomingLayout = normalizePlotlyLayout(layout)
    const themedLayout = createThemeLayout(themeColors, activeScheme)
    const baseLayout = {
      autosize: true,
      margin: {
        l: PLOTLY_THEME_DEFAULTS.margin,
        r: PLOTLY_THEME_DEFAULTS.margin,
        t: PLOTLY_THEME_DEFAULTS.marginTop,
        b: PLOTLY_THEME_DEFAULTS.margin,
      },
      showlegend: container.offsetWidth > PLOTLY_THEME_DEFAULTS.legendMinWidth,
      ...incomingLayout,
    }
    // Clamped after the spread so a figure's own margin can't leave its subplot titles nowhere to go.
    const sizedLayout = {
      ...baseLayout,
      margin: {
        ...baseLayout.margin,
        t: resolveTopMargin(baseLayout.margin?.t ?? PLOTLY_THEME_DEFAULTS.marginTop, incomingLayout),
      },
    }
    // Themed chrome overrides the incoming figure's baked colors/template; the deep merge preserves its
    // data-layout (axis range/type/title.text, annotations) since only chrome keys collide.
    const chartLayout = applyAxisThemeToLayout(deepMerge(sizedLayout, themedLayout), themedLayout.xaxis)

    const chartConfig = {
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: ["pan2d", "select2d", "lasso2d", "toImage"],
      ...(config || {}),
    }

    if (!isInitialized.current) {
      // `chartLayout`/`chartConfig` are freshly built each render, so cloning them stays cheap; `clonedData` is
      // the per-`data` clone reused across resize frames.
      await Plotly.newPlot(container, clonedData, structuredClone(chartLayout), structuredClone(chartConfig))
      isInitialized.current = true
    } else {
      await Plotly.react(container, clonedData, structuredClone(chartLayout), structuredClone(chartConfig))
    }
    return "done"
  }, [clonedData, layout, config, themeColors, activeScheme])

  // Serialize renders: `renderPlot` is async, so a slow `Plotly.react` could overlap the next frame's render on
  // the SAME container and interleave (Cursor r3668288331). An in-flight latch drops overlapping requests and
  // runs exactly one trailing render after the current one, picking up the latest render closure via the ref so
  // a `data`/theme change mid-render isn't rendered stale.
  const renderRef = useRef(renderPlot)
  const isRenderingRef = useRef(false)
  const pendingRenderRef = useRef(false)
  useLayoutEffect(() => { renderRef.current = renderPlot }, [renderPlot])
  const runRender = useCallback(async (container) => {
    if (isRenderingRef.current) {
      pendingRenderRef.current = true
      return
    }
    isRenderingRef.current = true
    try {
      do {
        pendingRenderRef.current = false
        let status = await renderRef.current(container).catch(() => "done")
        // Not renderable yet (zero-size / hidden): retry on the next frame while STILL holding the latch, so the
        // deferred render can't overlap a later serialized render. Re-reads renderRef for the latest closure.
        while (status === "not-renderable" && container?.isConnected) {
          await new Promise((resolve) => { requestAnimationFrame(resolve) })
          status = container.isConnected ? await renderRef.current(container).catch(() => "done") : "done"
        }
      } while (pendingRenderRef.current)
    } finally {
      isRenderingRef.current = false
    }
  }, [])

  // Resize runs the FULL themed render (rAF-coalesced to one/frame) rather than Plotly.Plots.resize: an
  // incremental resize doesn't recompute legend/title placement, so at narrow widths the legend could wedge
  // behind the title unrecoverably. A fresh layout each frame recomputes margins, automargin, the width-based
  // showlegend, and legend anchoring — it can never get stuck.
  const onResize = useCallback((container) => { void runRender(container) }, [runRender])

  usePlotlyResizeObserver({
    containerRef,
    isInitializedRef: isInitialized,
    onResize,
  })
  // `renderPlot` is the intentional re-trigger (data / layout / theme changed), not something the body reads:
  // `runRender` is stable, so tidying it out of the deps would make this mount-only. No lint rule guards it.
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }
    void runRender(container)
  }, [runRender, renderPlot])

  useLayoutEffect(() => {
    const container = containerRef.current
    return () => {
      try {
        if (container) {
          Plotly.purge(container)
        }
      } catch {
        // no-op on error
      }
      isInitialized.current = false
    }
  }, [])

  return <div ref={containerRef} className={classNames("plotly-chart", className)} />
}

PlotlyChart.propTypes = {
  data: PropTypes.array.isRequired,
  layout: PropTypes.object.isRequired,
  config: PropTypes.object,
  className: PropTypes.string,
}
